import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, ShieldAlert, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { authAxios, AuthShell } from "@/pages/Auth";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141C]/80 px-6 py-5">
      <p data-testid={testId} className="font-mono text-2xl font-bold text-white sm:text-3xl">{value}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
    </div>
  );
}

function LeadRow({ l }) {
  const q = l.quote;
  return (
    <tr data-testid={`lead-row-${l.id}`} className="border-t border-white/5 transition-colors hover:bg-white/[0.03]">
      <td className="px-4 py-4 align-top">
        <p className="text-sm font-semibold text-white">{l.name}</p>
        <p className="mt-0.5 text-xs text-slate-400">{l.email}</p>
        {l.phone && <p className="text-xs text-slate-400">{l.phone}</p>}
      </td>
      <td className="px-4 py-4 align-top text-sm text-slate-300">
        {q ? (
          <>
            <p>{q.summary.package}</p>
            <p className="text-xs text-slate-500">{q.summary.vehicle_type} · {q.summary.condition}</p>
            {q.summary.extras.length > 0 && <p className="text-xs text-slate-500">+ {q.summary.extras.join(", ")}</p>}
          </>
        ) : (
          <span className="text-xs text-slate-500">General enquiry</span>
        )}
        {l.vehicle && <p className="mt-1 text-xs text-[#FFB703]/80">{l.vehicle}</p>}
      </td>
      <td className="px-4 py-4 align-top font-mono text-sm text-[#FFB703]">{q ? `$${q.total.toLocaleString()}` : "-"}</td>
      <td className="px-4 py-4 align-top text-sm text-slate-300">{l.preferred_date || "-"}</td>
      <td className="px-4 py-4 align-top">
        {l.deposit_paid ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Paid</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><Clock className="h-3 w-3" /> None</span>
        )}
      </td>
      <td className="px-4 py-4 align-top text-xs text-slate-500">{fmtDate(l.created_at)}</td>
      <td className="max-w-[220px] px-4 py-4 align-top text-xs text-slate-400">{l.notes || ""}</td>
    </tr>
  );
}

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (authed) return;
    authAxios.get("/auth/me").then((r) => { setUser(r.data); setAuthed(true); }).catch(() => { setAuthed(false); navigate("/login", { replace: true }); });
  }, [authed, navigate]);

  useEffect(() => {
    if (!authed) return;
    authAxios.get("/admin/leads").then((r) => setData(r.data)).catch((e) => { if (e.response?.status === 403) setForbidden(true); });
  }, [authed]);

  const logout = async () => {
    await authAxios.post("/auth/logout").catch(() => {});
    navigate("/login", { replace: true });
  };

  if (authed === null) {
    return <AuthShell><Loader2 data-testid="dashboard-loading" className="mx-auto h-8 w-8 animate-spin text-[#FFB703]" /></AuthShell>;
  }
  if (forbidden) {
    return (
      <AuthShell>
        <div data-testid="dashboard-forbidden">
          <ShieldAlert className="mx-auto h-12 w-12 text-slate-500" />
          <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white">Not Authorised</h1>
          <p className="mt-4 text-sm text-slate-400">{user?.email} isn’t on the owner list for this dashboard.</p>
          <button type="button" onClick={logout} data-testid="dashboard-logout-button" className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703]">Sign out</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#090A0E] text-slate-200">
      <div className="grain-overlay" />
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-4"><img src="/media/logo.png" alt="Elite Mobile Detail" className="h-11 w-auto" /></Link>
          <div className="flex items-center gap-4">
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" />}
            <span data-testid="dashboard-user-email" className="hidden font-mono text-xs text-slate-400 sm:block">{user?.email}</span>
            <button type="button" onClick={logout} data-testid="dashboard-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-[#FFB703]/60 hover:text-[#FFB703]">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">Owner Dashboard</p>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">Quote <span className="text-[#FFB703]">Requests</span></h1>
        </motion.div>

        {!data ? (
          <Loader2 className="mt-12 h-6 w-6 animate-spin text-[#FFB703]" />
        ) : (
          <>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              <Stat label="Total requests" value={data.stats.total} testId="stat-total" />
              <Stat label="Deposits paid" value={data.stats.deposits_paid} testId="stat-deposits" />
              <Stat label="Quoted value" value={`$${data.stats.quoted_value.toLocaleString()}`} testId="stat-value" />
            </div>
            <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-[#12141C]/60">
              <table data-testid="leads-table" className="w-full min-w-[900px] text-left">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {["Customer", "Package", "Quote", "Preferred date", "Deposit", "Received", "Notes"].map((h) => (
                      <th key={h} className="px-4 py-4 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((l) => <LeadRow key={l.id} l={l} />)}
                  {data.leads.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">No quote requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        <Link to="/" className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
      </main>
    </div>
  );
}
